import type { ApiOptions, ApiRequest } from '@/types/api'
import stripTrackingParams from '@/helpers/strip-tracking-params'

const isTokenRequest = (request:Response) => {
  return request?.url?.includes('/auth/token')
}
const isLoginRequest = (request:Response) => {
  return request?.url?.includes('/auth/login')
}

const onRequest: any = async (request:Response) => {
  const options = request.options

  if (options?.params) {
    const whitelist = /[^A-Za-z0-9àáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑßÒÓÔÕÖØÙÚÛÜÝÞŸ\]\-[/&#_,.+=\s]/
    Object.keys(options.params).forEach((key:string) => {
      // Regex returns true when negative (^) match is found
      if (whitelist.test(options.params[key])) {
        console.error('Rejected param with key;', key, 'value = ', options.params[key], 'rejected char:', options.params[key].match(whitelist)[0])
        delete options.params[key]
      }

      if (whitelist.test(key)) {
        console.error('Rejected key ', key, 'rejected char:', key.match(whitelist)?.[0])
        delete options.params[key]
      }
    })
  }

  options.headers = options?.headers || {}
  if (process.server) {
    options.headers.set('user-agent', 'Server-agent')
    options.headers.set('access-control-allow-headers', 'Origin, X-Requested-With, Content-Type, Accept, Access-Control-Allow-Origin, Access-Control-Allow-Methods, ShopToken, Ggauthorization, Sentry-Trace, UnlockToken, Grecaptcha, GutsSource, WishlistId, GaClientId')
    options.headers.set('access-control-allow-methods', 'GET, POST, PUT, PATCH, DELETE, OPTIONS, HEAD')
    options.headers.set('user-agent', '*')
  }

  if (options?.params) {
    options.params = stripTrackingParams(options.params)
  }

  if (import.meta.client) {
    const rootStore = useRootStore()
    options.headers.set('Content-Language', rootStore.languages.includes(rootStore.lang) ? rootStore.lang : 'en')
  } else {
    const route = useRoute()
    options.headers.set('Content-Language', route.meta.forceLanguage || 'en')
  }
  // Add shoptoken to the request for instore mode
  if (import.meta.client) {
    const cartStore = useCartStore()
    const authStore = useAuthStore()

    // Cart headers
    if (cartStore.shopToken) {
      options.headers.set('ShopToken', cartStore.shopToken)
    }

    options.headers.set('Cart-Id', cartStore.cartId ? cartStore.cartId : null)

    // Auth headers
    // If a token is refreshing, wait till its done
    if (authStore.isRefreshing) {
      await new Promise((resolve, reject) => {
        watch(() => authStore.isRefreshing, isRefreshing => {
          if (!isRefreshing) {
            return resolve('Refresh done')
          }
        })
      })
    }

    // check if accessToken is valid, if so add Ggauthorization header
    const accessToken = await authStore.getValidAccessToken()
    if (accessToken && !options.disableAuthCheck) {
      options.headers.set('Ggauthorization', `Bearer ${accessToken}`)
    } else {
      delete options.headers.Ggauthorization
    }
  }

  if (options.disableAuthCheck) {
    return options
  }

  // In app context we add aditional headers\
  const appContext = useRuntimeConfig().public.appContext

  if (appContext) {
    options.headers.set('Guts-App-Context', appContext)

    if (import.meta.client) {
      const rootStore = useRootStore()
      if (rootStore.deviceId) {
        options.headers.set('Guts-Device-Id', rootStore.deviceId)
      }
    }
  }

  // Add ga client id to headers
  if (import.meta.client && useCookie('_ga').value) {
    options.headers.set('Ga-Client-Id', useCookie('_ga').value?.substring(6))
  }

  if (
    typeof window !== 'undefined' &&
    typeof options.recaptchaKey !== 'undefined' &&
    typeof options.headers.Grecaptcha === 'undefined'
  ) {
    await useRecaptcha()
    return new Promise((resolve, reject) => {
      if (typeof window.grecaptcha === 'undefined') { resolve(undefined) } // If recaptcha is not ready yet, just try it without recaptcha
      window.grecaptcha?.enterprise.ready(() => {
        window.grecaptcha?.enterprise.execute(useRuntimeConfig().public.recaptchaKey, {
          action: options.recaptchaKey || ''
        }).then((recaptchaToken:string) => {
          options.headers.set('Grecaptcha', recaptchaToken)
          resolve(recaptchaToken)
        }).catch(() => {
          console.log('recaptcha failed')
          return reject(new Error('Recaptcha failed'))
        })
      })
    })
  }
}

const onResponse: any = ({ response }: { response: Response }) => {
  if (import.meta.server) { return }
  const { limit, remaining, retryAt } = storeToRefs(useRateLimiterStore())
  if (response.headers.get('x-ratelimit-limit')) {
    limit.value = Number(response.headers.get('x-ratelimit-limit')) || 0
  }
  if (response.headers.get('x-ratelimit-remaining')) {
    remaining.value = Number(response.headers.get('x-ratelimit-remaining')) || 0
  }
  if (response.headers.get('x-ratelimit-reset')) {
    retryAt.value = response.headers.get('x-ratelimit-reset')
  }
  return true
}

const onResponseError: any = async ({ request, response, options }: { request: ApiRequest, response: Response, options: ApiOptions }) => {
  if (import.meta.server) { return }
  const authStore = useAuthStore()
  const rateLimiterStore = useRateLimiterStore()
  const { limit, remaining, retryAt } = storeToRefs(rateLimiterStore)

  if (response.headers.get('x-ratelimit-limit')) {
    limit.value = Number(response.headers.get('x-ratelimit-limit')) || 0
  }
  if (response.headers.get('x-ratelimit-remaining')) {
    remaining.value = Number(response.headers.get('x-ratelimit-remaining')) || 0
  }
  if (response.headers.get('x-ratelimit-reset')) {
    retryAt.value = response.headers.get('x-ratelimit-reset')
  }

  // If refreshToken call gives an error, reset the token.
  if (
    (response?.status === 401 && isTokenRequest(response)) ||
    (options?.retry)
  ) {
    return authStore.resetToken()
  }

  // Do nothing special when login attempt is not valid. Let login form handle it.
  if (await isLoginRequest(response) && response?.status === 401) {
    return true
  }

  // If an ajax-call is unauthorized and it is not a token refresh call, then try to refresh the token and repeat the ajax-call with a new token
  if (response?.status === 401 && !options.disableAuthCheck) {
    return await authStore.getValidAccessToken().then(async accessToken => {
      const config = options
      config.retry = true
      if (!process.server && accessToken) {
        config.headers?.set('Ggauthorization', `Bearer ${accessToken}`)
      } else {
        delete config.headers?.Ggauthorization // retry without accessToken
      }

      // @ts-ignore @Remco we use some other stuff in our API requests so I extended the NitroFetchOptions, although $fetch expects a NitroFetchOptions interface
      return await $fetch(request, config)
    })
  }
}

const api = (baseURL: string) => {
  return $fetch.create({
    baseURL,
    onRequest,
    onResponse,
    onResponseError
  }) as typeof $fetch
}

export default defineNuxtPlugin(() => {
  const baseURL = useRuntimeConfig().public.baseURL as String

  // @TODO: Fix typings

  return {
    provide: {
      api: api(baseURL)
    }
  }
})
